import { useFetchResolver } from '../ResolverHooks';
import { createStandardCachedResolver } from '../utils/createStandardCachedResolver';
import { isLoading, isError, isSuccess } from '../Status';
import { isObjectNotFoundError, isBatchObjectsNotFoundError } from '../Errors';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { SkeletonText } from './SkeletonText';
import { jsx as _jsx } from "react/jsx-runtime";
// Re-export in case anyone was using this
export { SkeletonText };
const defaultRenderLoading = () => /*#__PURE__*/_jsx(SkeletonText, {});
const defaultRenderError = ({
  id
}) => id;
const defaultRenderObjectNotFound = ({
  id
}) => id;
// NOTE: leave it up to calling apps to show a custom label for archived references
//       until this can be moved into ExternalOptions
//       See https://git.hubteam.com/HubSpot/ExternalOptions/pull/276
//       See https://git.hubteam.com/HubSpot/ExternalOptions/issues/196
const defaultRenderObjectArchived = ({
  reference
}) => reference.label || '--';
const defaultRenderSuccess = ({
  reference
}) => reference.label || '--';
const stopPropagation = e => {
  e.stopPropagation();
};
/**
 * Example:
 *   <ReferenceLiteName objectTypeId={ObjectTypeIds.DEAL_TYPE_ID} id={'123'} referenceType={ReferenceTypes.CRM_OBJECT} />
 * Renders as:
 *   My Deal Name
 *
 * If you already have a resolver (such as one from createPropertyResolver):
 *  <ReferenceLiteName resolver={resolver} id={'123'} />
 *
 * Content can be customized via the 'render<status>' render props:
 *   <ReferenceLiteName
 *     objectTypeId={ObjectTypeIds.DEAL_TYPE_ID}
 *     id={'123'}
 *     referenceType={ReferenceTypes.CRM_OBJECT}
 *     renderLoading={({ reference, referenceType, id }) => 'Loading...'}
 *     renderError={({ error, referenceType, id }) => 'An error occurred'}
 *     renderObjectNotFound={({ error, id }) => `${referenceType} id ${id} is missing or does not exist` }
 *     renderObjectArchived={({ reference, id }) => `${reference.label} (Deleted)` }
 *     renderSuccess={({ reference, referenceType, id }) => `${reference.label} is done!`}
 *   />
 *
 * Linking is supported via 'href':
 *   <ReferenceLiteName
 *     href={'/path/to/object'}
 *   />
 *
 * Wrapping resolved reference in additional copy via 'messageKey':
 *  // list.byName: "by {{reference}}"
 *  <ReferenceLiteName
 *    messageKey={'list.byName'}
 *  />
 * The supplied translation must include `{{reference}}`
 */
export const ReferenceLiteName = ({
  resolver: propsResolver,
  objectTypeId,
  referenceType: propsReferenceType,
  resolverOptions,
  id,
  renderLoading = defaultRenderLoading,
  renderError = defaultRenderError,
  renderObjectNotFound = defaultRenderObjectNotFound,
  renderObjectArchived = defaultRenderObjectArchived,
  renderSuccess = defaultRenderSuccess,
  href,
  messageKey
}) => {
  // TODO: Should not rely on the resolver's referenceType forever, as resolvers created using the
  // `/for-property` endpoints won't necessarily know their reference type up front. This is a
  // bigger problem than I'm willing to tackle in this PR, though.
  const referenceType = propsResolver ? propsResolver.referenceType : propsReferenceType;
  const resolver = propsResolver || createStandardCachedResolver(referenceType, objectTypeId, resolverOptions);
  const {
    reference,
    status,
    error
  } = useFetchResolver(resolver, `${id}`);
  let content = null;
  let showLink = !!href;
  if (isLoading(status)) {
    content = renderLoading({
      reference,
      referenceType,
      id
    });
  } else if (isError(status)) {
    if (isObjectNotFoundError(error) || isBatchObjectsNotFoundError(error)) {
      content = renderObjectNotFound({
        error,
        referenceType,
        id
      });
      // Don't link to unknown objects (does not exist).
      // Links will only take users to a 404.
      showLink = false;
    } else {
      content = renderError({
        error: error,
        referenceType,
        id
      });
    }
  } else if (isSuccess(status)) {
    if (!reference) {
      // if `id` is falsy, `useFetchResolver` will return success and an
      // `undefined` reference. There is nothing useful we can show.
      // Don't link, because links will only take users to a 404.
      content = null;
      showLink = false;
    } else if (reference.archived) {
      // Don't link to archived objects (deleted).
      // Links will only take users to a 404.
      content = renderObjectArchived({
        reference,
        referenceType,
        id
      });
      showLink = false;
    } else {
      content = renderSuccess({
        reference,
        referenceType,
        id
      });
    }
  }
  content = showLink ? /*#__PURE__*/_jsx(Link, {
    href: href,
    external: true,
    onClick: stopPropagation,
    children: content
  }) : /*#__PURE__*/_jsx("span", {
    children: content
  });
  content = messageKey ? /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: messageKey,
    options: {
      reference: content
    }
  }) : content;
  return content;
};
export default ReferenceLiteName;